<template>
<div class="supporting-keyword-root">
  <div>
    <div class="mt-10 location-lang-domain-area">
      <div>
        <div class="card-small-title">
          Select Location
        </div>
        <v-autocomplete outlined dense hide-details
          :items="availableLocations"
          item-text="location_name"
          item-value="location_code"
          v-model="selectedLocation"
          color="grayColor"
          class="mt-5">
        </v-autocomplete>
      </div>
      <div>
        <div class="card-small-title">
          Select Language
        </div>
        <v-autocomplete outlined dense hide-details
          :items="targetLanguages"
          v-model="selectedLanguage"
          item-text="language_name"
          item-value="language_code"
          color="grayColor"
          class="mt-5">
        </v-autocomplete>
      </div>
      <div>
        <div class="card-small-title">
          Domain name
        </div>
        <v-text-field outlined dense hide-details
          class="mt-5"
          color="grayColor"
          readonly
          v-model="domain"
        ></v-text-field>
      </div>
    </div>
    <div class="mt-10" style="max-width: 680px;">
      <div class="d-flex align-center gap10">
        <div class="card-small-title">
          Enter your target keyword or topic
        </div>
        <div class="flex-grow-1"></div>
      </div>
      <div class="customized-multiselect1">
        <v-text-field
          v-model="topic"
          placeholder="Enter the keyword or topic you wish to explore further"
          outlined dense hide-details
          class="search-query-input mt-2"
        />
      </div>
    </div>
    <div class="mt-10 d-flex align-center gap10">
      <v-btn rounded
        color="profileAnchorColor"
        class="button-with-icon text-none whiteColor--text"
        :disabled="isCalculating || !canStartSearchKeywords || (isWhiteGloveUser && !isMissionControlSession)"
        @click="() => startSearchKeywords()">
        <span>Search keywords</span>
      </v-btn>
    </div>
  </div>

  <div class="result-area mt-10">
    <div class="mt-10" v-if="researchReport && !isCalculating">
      <keyword-research-result
        :report="researchReport"
        reportType="SupportKeywordResearch"
        :showSiloExtraActions="false"
        title="Supporting Keyword Research"
        :researchHistory="researchHistory"
        :hasUnlimitedAccess="hasUnlimitedAccess"
        @onChangeResearchId="(id) => { selectedResearchReportId = id }"
        @onKeywordLabelUpdateSuccess="onKeywordLabelUpdateSuccess"
        @refreshReport="() => loadResearchReport()"
        @onAIResultReady="() => {reloadUser(); loadResearchReport()}"
      />
    </div>
  </div>
  
</div>
</template>

<script>
import Multiselect from 'vue-multiselect'

import dataForSeoLocations from '@/consts/dataforseo_locations.json';
import languages from '@/consts/dataforseo_languages.json';
import { getHostName, getPureServicePkgName } from '@/utils'
import { getSplittedKeywords } from '@/components/Campaigns/CommonFunctions.js'
import KeywordResearchResult from '@/components/Campaigns/KeywordResearchResult.vue';
import { mixin as export_mixin } from '@/components/export_mixin'
import {
  whiteGlovePlans,
} from "@/components/plans_constants"

const MAX_CLUSTERS_AI_FILTERING_ATTEMPTS = 10
const ALLOWED_KEYWORD_COUNT = 10

export default {
  mixins: [ export_mixin ],
  props: ['hasUnlimitedAccess'],
  components:  {
    Multiselect,
    KeywordResearchResult,
  },
  data () {
    return {

      selectedLocation: '',
      selectedLanguage: '',
      domain:'',
      topic: '',

      campaign: null,
      researchHistory: [],
      selectedResearchReportId: null,
      researchReport: null,

      isStartingAsyncTask: false,
      isAwaitingAsyncTaskStatus: false,
      aiTopicFieldValue: '',
      
    }
  },
  computed: {
    MAX_CLUSTERS_AI_FILTERING_ATTEMPTS() {
      return MAX_CLUSTERS_AI_FILTERING_ATTEMPTS
    },
    user() {
      return this.$store.state.user
    },
    isWhiteGloveUser () {
      if (!this.user) return false;

      const accountType = this.$store.state.selectedSubAccountRelation
        ?this.$store.state.selectedSubAccountRelation.ownerAccountType
        :this.user.accountType

      if (whiteGlovePlans.includes(getPureServicePkgName(accountType))) {
        return true;
      }
      return false;
    },
    isMissionControlSession () {
      return this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount
    },
    campaignId () {
      return parseInt(this.$route.params.campaignId || 0)
    },
    availableLocations () {
      return dataForSeoLocations.locations;
    },
    targetLanguages () {
      return languages;
    },
    canStartSearchKeywords () {
      return Boolean(this.topic)
    },
    asyncTasks() {
      return this.$store.state.asyncTasks
    },
    currentAsyncTask() {
      return this.asyncTasks.filter(task => task.id === this.researchReport?.taskId)[0]
    },
    isCalculating() {
      return this.isStartingAsyncTask || this.isAwaitingAsyncTaskStatus || this.currentAsyncTask?.is_pending_or_in_progress
    },
  },
  watch: {
    campaignId: function(newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.getCampaignById()
        this.loadSupportKeywordResearchHistoryForCampaign()
      }
    },
    selectedResearchReportId: function (newVal, oldVal) {
      if (newVal && newVal !== oldVal && newVal !== this?.researchReport?.id) {
        this.loadResearchReport()
      }
    },
    currentAsyncTask: function (newVal, oldVal) {
      if (newVal && !oldVal) {
        this.isAwaitingAsyncTaskStatus = false;
      }
      if (newVal?.is_completed && newVal?.is_completed !== oldVal?.is_completed) {
        this.reloadUser()
        this.loadResearchReport(true)
      }
    }
  },

  methods: {
    persistFilter(key) {
      if (this.researchReport) {
        this.$store.dispatch('updateSupportKeywordReport', {
          data: {
            researchId: this.researchReport.id,
            key: 'frontendFilters',
            value: {
              [key]: this[key]
            },
          },
          subAccountRelationId: this.subAccountRelationId
        })
      }
    },
    reloadUser() {
      // reload user to update ui with any credit usage
      this.$store.dispatch("loadUser").then(response => {
        this.$store.commit("setUser", response.data.user);
      });
    },
    loadResearchReport (scrollBottom = false) {
      return this.$store.dispatch('loadSupportKeywordResearchData', {
        researchId: this.selectedResearchReportId,
      })
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.researchReport = response.data.research;
          // this.keywords = this.researchReport.keywords
          this.selectedLanguage = this.researchReport.languageCode
          this.selectedLocation = parseInt(this.researchReport.locationCode || 0)
          
          this.topic = this.researchReport.targetTopics?.[0] || ''

          if (scrollBottom) {
            setTimeout(() => {
              document.querySelector(".result-area").scrollIntoView({
                behavior: 'smooth'
              });
            }, 500);
          }
        }
      })
    },
    getCampaignById () {
      this.$store.dispatch('getCampaignById', this.campaignId)
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            this.campaign = response.data.campaign
            // this.$store.commit('setCurrentCampaign', this.campaign)
            
            this.domain = getHostName(this.campaign.targetUrl)
            this.selectedLanguage = this.campaign.languageCode || 'en'
            if (this.campaign.locationCode) {
              this.selectedLocation = parseInt(this.campaign.locationCode || 0)
            }
          }
        })
    },
    loadSupportKeywordResearchHistoryForCampaign( scrollBottom = false ) {
      this.$store.dispatch('getSupportKeywordResearchHistoryForCampaign', {
        campaignId: this.campaignId
      })
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.researchHistory = response.data.researches
          if (this.researchHistory.length > 0 && (!this.researchReport || this.selectedResearchReportId < this.researchHistory[0].id )) {
            this.selectedResearchReportId = this.researchHistory[0].id
          }
          if (scrollBottom) {
            setTimeout(() => {
              document.querySelector(".result-area").scrollIntoView({
                behavior: 'smooth'
              });
            }, 500);
          }
        }
      })
    },
    
    startSearchKeywords () {

      const formData = {
        campaignId: this.campaign?.id || 0,
        topics: [this.topic],
        domain: this.domain,
        locationCode: this.selectedLocation,
        languageCode: this.selectedLanguage,
        minWordsLimit: 3,
        maxWordsLimit: 100,
      }

      this.isStartingAsyncTask = true
      this.$store.dispatch('getKeywordsFromTopic', formData)
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.$store.commit('triggerAsyncTasksRefresh');
          this.researchReport = response.data.report
          this.selectedResearchReportId = response.data.report.id
          this.isAwaitingAsyncTaskStatus = true;
          this.isStartingAsyncTask = false
          this.$notify({
            group: 'info', type: 'success',
            text: 'Keyword research task has started!'
          })
        } else {
          this.isStartingAsyncTask = false;
          this.$notify({
            group: 'info', type: 'error',
            text: response?.data?.msg || 'Failed to create keyword research task'
          })
        }
      })
      .catch(() => {
        this.isStartingAsyncTask = false
      })
    },
    onKeywordLabelUpdateSuccess(keywords, label, resetExistingTopLevelKeywords) {
      if (label === 'T') {
        throw 'Supporting Keyword Research does not accept Top-Level Keywords'
      }
      this.researchReport = {
        ...this.researchReport,
        result: this.researchReport.result.map((row) => {
          if (keywords.includes(row.keyword)) {
            return {
              ...row,
              isSupportingKeyword: label === 'S',
            }
          }
          return row
        })
      }
    },
    handleStickyKeywordsListHeader () {
      const el = document.querySelector(".sticky-keywords-list-header");
      if (el) {
        const mainBodyTop = document.querySelector('#mainBodyScrollArea').getBoundingClientRect().top;
        const currentTop = el.getBoundingClientRect().top;
        el.classList.toggle("is-pinned", currentTop === mainBodyTop);
      }
    }
  },
  created () {
    document.querySelector('#mainBodyScrollArea').addEventListener('scroll', this.handleStickyKeywordsListHeader);
  },
  destroyed () {
    document.querySelector('#mainBodyScrollArea').removeEventListener('scroll', this.handleStickyKeywordsListHeader);
  },
  mounted () {
    if (!this.campaign) {
      this.getCampaignById()
      this.loadSupportKeywordResearchHistoryForCampaign()
    }

  },
}
</script>



<style lang="scss" scoped>
@import "../../../keyword-research.scss";

.general-info {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
}

.cluster-box {
  .cluster-header {
    display: flex;
    align-items: center;
    gap: 15px;

    .expand-icon {
      cursor: pointer;
    }
    .cluster-name {
      font-weight: 700;
      font-size: 1.1rem;
      color: var(--v-mainColor-base);
    }
    .keywords-count {
      padding: 5px 10px;
      border-radius: 5px;
      background: var(--v-lightYellowColor-base);
      color: var(--v-profileAnchorColor-base);
      font-weight: 700;
    }
    .best-match-label {
      padding: 5px 10px;
      border-radius: 40px;
      background: #CDF7E3;
      color: #006843;
      font-weight: 700;
    }
  }
  .cluster-body {
    max-height: 350px;
    overflow-y: auto;
    .keywords-wrapper {
      // display: flex;
      // flex-wrap: wrap;
      // gap: 10px;
      & > div {
        margin-top: 10px;
        display: flex;
      }
      & .one-keyword-item {
        background: var(--v-gray7Color-base);
        color: var(--v-mainColor-base);
        padding: 5px 10px;
        border-radius: 50vh;
        display: flex;
        align-items: center;
        gap: 5px;
        span.close {
          color: var(--v-profileAnchorColor-base);
          cursor: pointer;
        }
      }
    }
  }
}
.remove-cluster-icon {
  color: var(--v-profileAnchorColor-base);
  cursor: pointer;
}

.keyword-data-cost-modal, .search-keywords-cost-modal {
  .message-text, .message-text * {
    font-size: 1rem;
    line-height: 1.5rem;
  }
}

</style>